import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Factory,
  Ship,
  Layers,
  Calendar,
  Cable,
  LayoutGrid,
  Footprints,
  Car,
  Stethoscope,
  Package,
  Tent,
  ToyBrick,
  HardHat,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { BlobBackdrop } from "@/components/Blobs";
import {
  COMPANY,
  HERO_IMAGE,
  PLANT_IMAGE,
  INDUSTRIES,
  PRODUCTS,
} from "@/lib/siteData";

const iconMap = {
  Cable,
  LayoutGrid,
  Footprints,
  Car,
  Stethoscope,
  Package,
  Layers,
  Tent,
  ToyBrick,
  HardHat,
};

export default function Home() {
  return (
    <div data-testid="home-page">
      {/* HERO */}
      <section
        className="relative overflow-hidden border-b border-slate-100"
        data-testid="home-hero"
      >
        <BlobBackdrop />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 pt-20 pb-24 lg:pt-28 lg:pb-32 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 fade-up">
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-indigo-600 mb-5">
              50 Years of Chemical Legacy
            </p>
            <h1 className="font-display text-5xl sm:text-6xl lg:text-7xl font-bold uppercase tracking-tight text-slate-900 leading-[1.02]">
              Plasticisers,
              <br />
              <span className="gradient-text">engineered for every industry.</span>
            </h1>
            <p className="mt-6 text-lg text-slate-600 max-w-xl leading-relaxed">
              Backed by our parent company's 50-year legacy in chemical
              manufacturing, we produce high-performance plasticisers — DOTP
              · DOP · DINP · TOTM — with strategic port access, catering to
              multiple industries across India.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <Link to="/quote" data-testid="hero-quote-link">
                <Button
                  data-testid="hero-quote-cta"
                  className="gradient-accent text-white font-semibold uppercase tracking-wider rounded-md hover:opacity-90 transition-opacity h-12 px-7"
                >
                  Get a Quote
                  <ArrowRight className="ml-2" size={18} />
                </Button>
              </Link>
              <Link to="/products" data-testid="hero-products-link">
                <Button
                  variant="outline"
                  data-testid="hero-products-cta"
                  className="border-slate-300 text-slate-900 hover:bg-slate-50 font-semibold uppercase tracking-wider rounded-md h-12 px-7"
                >
                  View Products
                </Button>
              </Link>
            </div>
          </div>

          <div className="lg:col-span-5 fade-up space-y-4">
            <div
              className="relative rounded-2xl overflow-hidden shadow-xl border border-slate-100"
              data-testid="hero-plant-image"
            >
              <img
                src={PLANT_IMAGE}
                alt="RPG Rasayan Udyog manufacturing facility"
                className="w-full h-[260px] object-cover"
              />
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(10,25,47,0.55) 0%, rgba(79,70,229,0.35) 100%)",
                }}
              />
              <div className="absolute bottom-5 left-5 right-5 text-white">
                <p className="text-xs uppercase tracking-[0.25em] opacity-80">
                  Our plant
                </p>
                <p className="font-display text-2xl uppercase mt-1">
                  Eastern India · State-of-the-Art Facility
                </p>
              </div>
            </div>
            <div
              className="relative rounded-2xl overflow-hidden shadow-xl border border-slate-100"
              data-testid="hero-port-image"
            >
              <img
                src={HERO_IMAGE}
                alt="Haldia port logistics access for RPG Rasayan Udyog"
                className="w-full h-[150px] object-cover"
              />
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "linear-gradient(135deg, rgba(10,25,47,0.55) 0%, rgba(79,70,229,0.35) 100%)",
                }}
              />
              <div className="absolute bottom-4 left-5 right-5 text-white">
                <p className="font-display text-lg uppercase">
                  Strategic Port Access for Raw Material Sourcing
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CAPACITY STATS */}
      <section
        className="relative py-20 bg-slate-50 border-y border-slate-100"
        data-testid="home-stats"
      >
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            <StatBlock
              testid="stat-capacity"
              label="Current Capacity"
              value={`${COMPANY.capacityNow}`}
              unit="tonnes / year"
              note="Plasticisers"
            />
            <StatBlock
              testid="stat-expansion"
              label="Planned Expansion"
              value={`${COMPANY.capacityFuture}`}
              unit="tonnes / year"
              note={`within ${COMPANY.expansionYears} years`}
            />
            <StatBlock
              testid="stat-legacy"
              label="Parent Legacy"
              value={`${COMPANY.parentLegacyYears}`}
              unit="years"
              note="of chemical industry expertise"
            />
            <StatBlock
              testid="stat-delivery"
              label="Delivery Promise"
              value="Same Day"
              unit="Dispatch, Pan-India"
              note="We value your order and your time."
            />
          </div>
        </div>
      </section>

      {/* VALUE PROPS */}
      <section className="relative py-24" data-testid="home-value-props">
        <BlobBackdrop variant="subtle" />
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-indigo-600 mb-3">
            Why RPG Rasayan Udyog
          </p>
          <h2 className="font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight text-slate-900 max-w-3xl">
            Backed by 50 years of chemical legacy, now manufacturing plasticisers.
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-14">
            <ValueCard
              testid="value-pioneer"
              icon={<Factory className="text-indigo-600" />}
              title="First in Eastern India"
              body="Backed by our parent company's 50-year chemical manufacturing legacy — now the region's first dedicated plasticiser manufacturer."
            />
            <ValueCard
              testid="value-port"
              icon={<Ship className="text-fuchsia-600" />}
              title="Strategic Port Access"
              body="Direct port logistics enables competitive raw material sourcing — savings we pass on to our customers."
            />
            <ValueCard
              testid="value-scale"
              icon={<Layers className="text-pink-600" />}
              title="State-of-the-Art Scale"
              body="Modern process design, built to scale from 30,000 to 50,000 tonnes/year of plasticiser capacity."
            />
          </div>
        </div>
      </section>

      {/* PRODUCTS PREVIEW */}
      <section
        className="relative py-24 bg-slate-50 border-y border-slate-100"
        data-testid="home-products"
      >
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12 gap-4">
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.3em] text-indigo-600 mb-3">
                Our Product Range
              </p>
              <h2 className="font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight text-slate-900">
                Four plasticisers. Engineered for performance.
              </h2>
            </div>
            <Link to="/products" data-testid="home-products-all-link">
              <Button
                variant="outline"
                className="border-slate-300 font-semibold uppercase tracking-wider"
              >
                View All Products <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {PRODUCTS.map((p) => (
              <Link
                to="/products"
                key={p.code}
                data-testid={`home-product-card-${p.code.toLowerCase()}`}
                className="group bg-white rounded-xl border border-slate-100 p-6 hover:shadow-lg transition-all duration-300 hover:-translate-y-1"
              >
                <div className="h-1 w-12 gradient-accent rounded-full mb-5" />
                <p className="font-display text-3xl font-bold uppercase text-slate-900">
                  {p.code}
                </p>
                <p className="text-sm text-slate-500 mt-1">{p.name}</p>
                <p className="mt-4 text-sm text-slate-600 leading-relaxed line-clamp-3">
                  {p.description}
                </p>
                <p className="mt-5 text-xs uppercase tracking-wider font-semibold text-indigo-600 inline-flex items-center group-hover:gap-2 gap-1 transition-all">
                  Learn More <ArrowRight size={14} />
                </p>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* INDUSTRIES */}
      <section className="relative py-24" data-testid="home-industries">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-indigo-600 mb-3">
            Industries We Serve
          </p>
          <h2 className="font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight text-slate-900 max-w-3xl">
            Ten industries. One trusted supplier.
          </h2>

          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 mt-14">
            {INDUSTRIES.map((ind) => {
              const Icon = iconMap[ind.icon] || Layers;
              return (
                <div
                  key={ind.name}
                  data-testid={`home-industry-${ind.name.toLowerCase().replace(/\s+/g, "-")}`}
                  className="group relative bg-white border border-slate-100 rounded-xl p-5 hover:shadow-md transition-all cursor-default overflow-hidden"
                >
                  <div className="absolute inset-x-0 top-0 h-0.5 gradient-accent opacity-0 group-hover:opacity-100 transition-opacity" />
                  <div className="w-10 h-10 rounded-lg gradient-accent-soft flex items-center justify-center text-indigo-600 mb-3">
                    <Icon size={20} />
                  </div>
                  <p className="font-semibold text-slate-900 text-sm leading-snug">
                    {ind.name}
                  </p>
                </div>
              );
            })}
          </div>
          <div className="mt-10">
            <Link to="/industries" data-testid="home-industries-link">
              <Button
                variant="outline"
                className="border-slate-300 font-semibold uppercase tracking-wider"
              >
                Explore Industries <ArrowRight size={16} className="ml-2" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* EXPANSION TIMELINE TEASER */}
      <section
        className="relative py-24 bg-navy text-white overflow-hidden"
        data-testid="home-roadmap"
      >
        <div className="absolute inset-0 pointer-events-none">
          <div
            className="absolute -top-32 -right-20 w-[520px] h-[520px] rounded-full blur-3xl opacity-30 animate-blob"
            style={{
              background:
                "radial-gradient(circle at 50% 50%, #6366f1, transparent 60%)",
            }}
          />
          <div
            className="absolute -bottom-24 -left-10 w-[460px] h-[460px] rounded-full blur-3xl opacity-25 animate-blob"
            style={{
              background:
                "radial-gradient(circle at 50% 50%, #ec4899, transparent 60%)",
              animationDelay: "3s",
            }}
          />
        </div>
        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.3em] text-indigo-300 mb-3">
              Capacity Roadmap
            </p>
            <h2 className="font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight">
              30,000 today.
              <br />
              <span className="gradient-text">50,000 within 3 years.</span>
            </h2>
            <p className="mt-5 text-slate-300 max-w-lg leading-relaxed">
              Backed by a parent company with five decades of chemical
              manufacturing, we are scaling our plasticiser capacity
              deliberately to meet the rising demand from India's flexible
              PVC value chain.
            </p>
            <Link to="/about" data-testid="home-about-link">
              <Button className="mt-8 gradient-accent text-white font-semibold uppercase tracking-wider rounded-md h-12 px-7">
                Learn More About Us <ArrowRight size={18} className="ml-2" />
              </Button>
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-4">
            <RoadmapStep
              testid="roadmap-now"
              label="Today"
              value="30,000 t / yr"
              note="Operational capacity"
            />
            <RoadmapStep
              testid="roadmap-mid"
              label="Phase 2"
              value="40,000 t / yr"
              note="Debottlenecking + expansion"
            />
            <RoadmapStep
              testid="roadmap-end"
              label={`Within ${COMPANY.expansionYears} yrs`}
              value="50,000 t / yr"
              note="Target installed capacity"
            />
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="relative py-24" data-testid="home-cta">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 text-center">
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-indigo-600 mb-4">
            Let's Talk Volume
          </p>
          <h2 className="font-display text-4xl sm:text-5xl font-bold uppercase tracking-tight text-slate-900">
            Ready to source plasticisers with structural cost advantage?
          </h2>
          <p className="mt-5 text-slate-600 max-w-2xl mx-auto">
            Tell us your application, monthly volume, and destination — we'll
            come back with technical data and a price indication.
          </p>
          <Link to="/quote" data-testid="home-cta-quote-link">
            <Button className="mt-8 gradient-accent text-white font-semibold uppercase tracking-wider rounded-md h-12 px-8">
              Request a Quote <ArrowRight className="ml-2" size={18} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}

const StatBlock = ({ label, value, unit, note, testid }) => (
  <div
    className="bg-white rounded-2xl p-8 border border-slate-100 shadow-sm relative overflow-hidden"
    data-testid={testid}
  >
    <div className="absolute -top-10 -right-10 w-32 h-32 rounded-full blur-2xl opacity-20 gradient-accent" />
    <p className="text-xs font-semibold uppercase tracking-[0.25em] text-indigo-600">
      {label}
    </p>
    <p className="font-display text-6xl font-bold text-slate-900 mt-3 leading-none">
      {value}
    </p>
    <p className="text-sm text-slate-500 mt-2">{unit}</p>
    <p className="text-xs text-slate-400 mt-1">{note}</p>
  </div>
);

const ValueCard = ({ icon, title, body, testid }) => (
  <div
    className="bg-white rounded-2xl border border-slate-100 p-7 hover:shadow-md transition-shadow group relative overflow-hidden"
    data-testid={testid}
  >
    <div className="w-12 h-12 rounded-xl gradient-accent-soft flex items-center justify-center mb-5">
      {icon}
    </div>
    <h3 className="font-display text-xl font-bold uppercase text-slate-900 tracking-tight">
      {title}
    </h3>
    <p className="mt-3 text-sm text-slate-600 leading-relaxed">{body}</p>
    <div className="absolute inset-x-0 bottom-0 h-1 gradient-accent opacity-0 group-hover:opacity-100 transition-opacity" />
  </div>
);

const RoadmapStep = ({ label, value, note, testid }) => (
  <div
    className="bg-white/5 backdrop-blur rounded-xl p-6 border border-white/10"
    data-testid={testid}
  >
    <div className="flex items-center justify-between gap-4">
      <div>
        <p className="text-xs uppercase tracking-widest text-indigo-300 flex items-center gap-2">
          <Calendar size={14} /> {label}
        </p>
        <p className="font-display text-3xl font-bold mt-1">{value}</p>
        <p className="text-sm text-slate-300 mt-1">{note}</p>
      </div>
    </div>
  </div>
);
